import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useRevertTaskResource, useUnAssignTaskResource, useUpdateTaskResource } from 'src/api/tasks'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { getFormattedDate } from 'src/utils'
import EditTaskResourceForm from '../../customer/tasks/tasks/EditTaskResourceForm'
import { IVesselDisplayRow } from './vesselSuccession'

export const useVesselActions = ({
  localityId,
  taskId,
  workOrderStart,
  workOrderEnd,
  onChanged,
}: {
  localityId: EntityId
  taskId: EntityId
  workOrderStart?: Date
  workOrderEnd?: Date
  onChanged: () => void
}) => {
  const { t: translate } = useTranslation(['task', 'common', 'common-api-message'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { mutateAsync: mutateUpdateTaskResource } = useUpdateTaskResource(localityId, taskId)
  const { mutateAsync: mutateRevertTaskResource } = useRevertTaskResource(localityId, taskId)
  const { mutateAsync: mutateUnAssignTaskResource } = useUnAssignTaskResource(localityId, taskId)

  const onEdit = useCallback(
    (row: IVesselDisplayRow) => {
      showDialog(EditTaskResourceForm, {
        title: translate('edit-vessel', { ns: 'task' }),
        opts: { maxWidth: 'sm', fullWidth: true },
        componentProps: {
          taskResource: row.taskResource,
          predecessor: row.predecessor,
          successor: row.successor,
          workOrderStart,
          workOrderEnd,
          allowDescription: row.isHead,
          onSubmit: async formValues => {
            await mutateUpdateTaskResource(
              {
                taskResourceId: row.taskResource.id,
                startDate: formValues.startDate,
                endDate: formValues.endDate,
                description: formValues.description,
              },
              {
                onSuccess: () => {
                  toast.success(translate('successfullyUpdated', { ns: 'common-api-message' }))
                  onChanged()
                },
                onError: () => {
                  toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
                },
              },
            )
          },
        },
      })
    },
    [mutateUpdateTaskResource, onChanged, showDialog, translate, workOrderEnd, workOrderStart],
  )

  const onRevert = useCallback(
    (row: IVesselDisplayRow) => {
      showConfirmDialog(
        translate('revert-vessel-title', { ns: 'task' }),
        translate('revert-vessel-description', {
          ns: 'task',
          vessel: row.name,
          replaced: row.predecessor?.resource?.name ?? row.predecessor?.resourceName ?? '',
          date: getFormattedDate(row.endDate),
        }),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await mutateRevertTaskResource(row.taskResource.id, {
            onSuccess: () => {
              toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
              onChanged()
            },
            onError: () => {
              toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
            },
          })
          pop()
        },
      )
    },
    [mutateRevertTaskResource, onChanged, showConfirmDialog, translate],
  )

  const onDelete = useCallback(
    (row: IVesselDisplayRow) => {
      showConfirmDialog(
        translate('unassign-resource-title', { ns: 'task' }),
        translate('unassign-resource-description', { ns: 'task' }),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await mutateUnAssignTaskResource(row.taskResource.id, {
            onSuccess: () => {
              toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
              onChanged()
            },
            onError: () => {
              toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
            },
          })
          pop()
        },
      )
    },
    [mutateUnAssignTaskResource, onChanged, showConfirmDialog, translate],
  )

  return { onEdit, onRevert, onDelete }
}
